import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from '../../i18n.ts';

interface LegalPagesProps {
  page: 'privacy' | 'terms';
  lang: string;
}

const LegalPages: React.FC<LegalPagesProps> = ({ page, lang }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Scroll to top whenever the page prop changes
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto no-scrollbar scroll-smooth bg-pt-cream">
      <div className="max-w-[900px] mx-auto px-6 py-12">
        {/* Back link */}
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-sm font-semibold text-pt-forest/60 hover:text-pt-forest transition-colors mb-10 group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          {t('dashboard')}
        </button>

        {page === 'privacy' ? <PrivacyPolicy /> : <TermsOfService />}

        {/* Version footer */}
        <div className="mt-16 pt-8 border-t border-pt-forest/10 text-center">
          <p className="text-xs text-pt-forest/40 font-medium tracking-wide">
            Versie 1.0 — Februari 2026
          </p>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Privacy Policy (Privacybeleid)                                            */
/* -------------------------------------------------------------------------- */

const PrivacyPolicy: React.FC = () => (
  <article className="prose-pt">
    <h1>Privacybeleid</h1>
    <p className="lead">
      Dit privacybeleid beschrijft hoe Pecktree uw persoonsgegevens verwerkt in overeenstemming met
      de Algemene Verordening Gegevensbescherming (AVG/GDPR). Laatst bijgewerkt: oktober 2026.
    </p>

    {/* 1 */}
    <h2>1. Verwerkingsverantwoordelijke</h2>
    <p>
      Pecktree is een dienst van <strong>Ariens Design</strong>, gevestigd te Zaltbommel
      (Nederland), ingeschreven in het Handelsregister van de Kamer van Koophandel onder nummer{' '}
      <strong>30274157</strong>, btw-identificatienummer <strong>NL001532302B39</strong>.
    </p>
    <p>
      Ariens Design is verwerkingsverantwoordelijke voor de gegevens die nodig zijn om de dienst te
      leveren: uw accountgegevens, uw abonnement en de technische gegevens uit sectie 2.
    </p>
    <p>
      Voor de genealogische gegevens die u zelf invoert bent <strong>u</strong> de
      verwerkingsverantwoordelijke. Ariens Design verwerkt die gegevens uitsluitend om de dienst aan
      u te kunnen leveren en handelt daarbij volgens uw instructies.
    </p>
    <p>
      Voor vragen over gegevensverwerking kunt u contact opnemen via{' '}
      <strong>privacy@pecktree.nl</strong>.
    </p>

    {/* 2 */}
    <h2>2. Welke gegevens worden verwerkt</h2>
    <p>Wij verwerken de volgende categorie&euml;n persoonsgegevens:</p>
    <ul>
      <li>
        <strong>Accountgegevens</strong> — e-mailadres en een gehashte versie van uw wachtwoord
        (bcrypt). Uw wachtwoord wordt nooit in leesbare vorm opgeslagen.
      </li>
      <li>
        <strong>Genealogische gegevens</strong> — namen, geboorte- en overlijdensdata, plaatsnamen,
        beroepen, relaties, bronvermeldingen en eventueel door u geüploade media. Deze gegevens
        worden uitsluitend opgeslagen binnen uw projecten.
      </li>
      <li>
        <strong>Technische gegevens</strong> — IP-adressen, user-agent-strings en tijdstempels van
        verzoeken ten behoeve van beveiliging en foutopsporing.
      </li>
      <li>
        <strong>Cookies</strong> — zie sectie 7 hieronder.
      </li>
    </ul>

    {/* 3 */}
    <h2>3. Rechtsgrond voor verwerking</h2>
    <p>Wij verwerken uw gegevens op basis van de volgende rechtsgronden:</p>
    <ul>
      <li>
        <strong>Uitvoering van de overeenkomst</strong> (art.&nbsp;6 lid&nbsp;1 sub&nbsp;b AVG) — uw
        accountgegevens en genealogische data worden verwerkt om de dienst aan u te kunnen leveren.
      </li>
      <li>
        <strong>Gerechtvaardigd belang</strong> (art.&nbsp;6 lid&nbsp;1 sub&nbsp;f AVG) — technische
        gegevens en logbestanden worden verwerkt om de beveiliging en stabiliteit van het platform
        te waarborgen.
      </li>
    </ul>

    {/* 4 */}
    <h2>4. Bewaartermijnen</h2>
    <ul>
      <li>
        <strong>Accountgegevens</strong> — worden bewaard zolang uw account actief is. Na
        verwijdering van uw account worden alle gegevens binnen 30 dagen definitief verwijderd.
      </li>
      <li>
        <strong>Genealogische gegevens</strong> — worden bewaard zolang het betreffende project
        bestaat. U kunt projecten op elk moment verwijderen. Een verwijderd project staat dan 30
        dagen in de prullenbak, zodat u een vergissing kunt herstellen. Daarna is het niet meer zelf
        terug te halen en verdwijnen de gegevens binnen 90 dagen definitief uit onze database. Wilt
        u niet wachten, dan wist de optie &ldquo;definitief verwijderen&rdquo; alles direct.
      </li>
      <li>
        <strong>Logbestanden</strong> — worden maximaal 90 dagen bewaard en daarna automatisch
        verwijderd.
      </li>
    </ul>

    {/* 5 */}
    <h2>5. Uw rechten als betrokkene</h2>
    <p>Op grond van de AVG heeft u de volgende rechten:</p>
    <ul>
      <li>
        <strong>Recht op inzage</strong> — u kunt opvragen welke gegevens wij van u verwerken.
      </li>
      <li>
        <strong>Recht op rectificatie</strong> — u kunt onjuiste gegevens laten corrigeren.
      </li>
      <li>
        <strong>Recht op verwijdering</strong> — u kunt verzoeken om verwijdering van al uw
        gegevens.
      </li>
      <li>
        <strong>Recht op dataportabiliteit</strong> — u kunt uw genealogische gegevens exporteren in
        GEDCOM 5.5.1-formaat via de exportfunctie.
      </li>
      <li>
        <strong>Recht van bezwaar</strong> — u kunt bezwaar maken tegen verwerking op basis van
        gerechtvaardigd belang.
      </li>
    </ul>
    <p>
      Al deze rechten kunt u uitoefenen via uw <strong>accountpagina</strong> binnen het platform,
      of door contact op te nemen met de verwerkingsverantwoordelijke.
    </p>

    {/* 6 */}
    <h2>6. Delen met derden</h2>
    <p>
      Pecktree verkoopt uw gegevens niet en deelt ze niet voor commerci&euml;le doeleinden. Om de
      dienst te kunnen leveren schakelen wij wel een aantal andere partijen in. Hieronder staan ze
      allemaal. De uitgebreide versie &mdash; met vestigingsland en grondslag voor doorgifte per
      partij &mdash; is de bijlage bij onze verwerkersovereenkomst en kunt u opvragen via{' '}
      <a href="mailto:privacy@pecktree.nl" className="text-pt-gold-text hover:underline">
        privacy@pecktree.nl
      </a>
      .
    </p>
    <p>In hoofdlijnen gaat het om:</p>
    <ul>
      <li>
        <strong>Hosting op eigen apparatuur</strong> &mdash; de applicatie, de database en uw
        bestanden staan op onze eigen servers in Nederland. Dat geldt ook voor het omzetten van
        plaatsnamen: dat draait lokaal en verlaat onze infrastructuur niet.
      </li>
      <li>
        <strong>Cloudflare</strong> &mdash; verzorgt de beveiligde verbinding en de bescherming
        tegen aanvallen. Al het verkeer naar Pecktree loopt hierlangs. Meet ook, zonder cookies, hoe
        snel de pagina&apos;s laden (zie hoofdstuk 7).
      </li>
      <li>
        <strong>Stripe</strong> &mdash; verwerkt betalingen en abonnementen. Wij slaan zelf geen
        kaartgegevens op.
      </li>
      <li>
        <strong>ZXCS</strong> &mdash; verzorgt onze e-mail, zoals wachtwoordherstel en
        uitnodigingen.
      </li>
      <li>
        <strong>Google</strong> &mdash; voor het omzetten van plaatsnamen naar
        kaartco&ouml;rdinaten, en voor wie ervoor kiest: inloggen met een Google-account. Bij
        plaatsbepaling gaat alleen de plaatsnaam mee.
      </li>
      <li>
        <strong>Open Archieven</strong> &mdash; Pecktree zoekt archiefbronnen in een eigen
        archiefindex. Alleen als die tijdelijk niet bereikbaar is, gaat uw zoekopdracht naar deze
        Nederlandse stichting.
      </li>
      <li>
        <strong>OpenStreetMap / Nominatim</strong> &mdash; alternatieve dienst voor plaatsnamen. Ook
        hier gaat alleen de plaatsnaam mee.
      </li>
    </ul>

    {/* 7 */}
    <h2>7. Cookies</h2>
    <p>
      Pecktree plaatst uitsluitend cookies die nodig zijn om de dienst te laten werken. Er staan
      geen advertentiecookies op uw apparaat. Van derden komen alleen de twee cookies onderaan dit
      lijstje: die van Cloudflare en die van de knop Inloggen met Google.
    </p>
    <ul>
      <li>
        <code>auth_token</code> &mdash; houdt u ingelogd, niet leesbaar voor scripts in de browser.
        Bewaartijd 15 minuten.
      </li>
      <li>
        <code>refresh_token</code> &mdash; verlengt uw sessie, zodat u niet elk kwartier opnieuw
        hoeft in te loggen. Bewaartijd 30 dagen.
      </li>
      <li>
        <code>csrf_token</code> &mdash; beschermt tegen verzoeken die een andere website namens u
        zou proberen te doen. Bewaartijd 24 uur.
      </li>
      <li>
        <code>two_factor_token</code> &mdash; alleen tijdens het invoeren van uw tweede inlogfactor.
        Bewaartijd 5 minuten.
      </li>
      <li>
        <code>cf_clearance</code> &mdash; van Cloudflare, dat Pecktree beschermt tegen aanvallen en
        misbruik door robots. Onthoudt dat uw browser die controle heeft doorstaan. Bewaartijd 1
        jaar.
      </li>
      <li>
        <code>g_state</code> &mdash; geplaatst door het script van Google achter de knop Inloggen
        met Google op de inlogpagina. Onthoudt of de inlogsuggestie van Google al getoond of
        weggeklikt is. Niet voor statistieken of advertenties. Bewaartijd 180 dagen.
      </li>
    </ul>
    <p>
      Daarnaast onthoudt uw browser in de lokale opslag welk project u het laatst open had en welke
      keuze u in het toestemmingsvenster maakte. Dat blijft op uw eigen apparaat staan en wordt niet
      naar ons verstuurd.
    </p>
    <p>
      Bij uw eerste bezoek toont het platform een venster waarin u een keuze maakt over
      gebruiksstatistieken. Die keuze slaan wij bij uw account op.
    </p>
    <p>
      Cloudflare meet met een klein script hoe snel de pagina&apos;s van Pecktree laden. Dat script
      plaatst niets in uw browser, leest geen cookies of andere opgeslagen gegevens en herkent u
      niet op een volgend bezoek. Cloudflare ontvangt alleen welke pagina u bekijkt en hoe snel die
      laadt. Uw IP-adres komt mee met het verzoek, maar Cloudflare gooit het in het dichtstbijzijnde
      datacenter weg en bewaart het niet. Omdat er geen cookie of vergelijkbare techniek aan te pas
      komt, vragen wij hiervoor geen toestemming.
    </p>

    {/* 8 */}
    <h2>8. Beveiliging</h2>
    <p>
      Wij nemen de bescherming van uw gegevens serieus en hanteren de volgende
      beveiligingsmaatregelen:
    </p>
    <ul>
      <li>
        Wachtwoorden worden opgeslagen met <strong>bcrypt</strong>-hashing (cost factor 12).
      </li>
      <li>
        Authenticatie verloopt via <strong>httpOnly-cookies</strong>, ontoegankelijk voor
        client-side JavaScript.
      </li>
      <li>
        Alle communicatie verloopt via <strong>HTTPS</strong> in productie.
      </li>
      <li>Databasetoegang is beperkt tot het applicatieserviceaccount.</li>
      <li>Invoervalidatie en parameterized queries ter voorkoming van SQL-injectie.</li>
    </ul>

    {/* 9 */}
    <h2>9. Contact en toezichthouder</h2>
    <p>
      Voor vragen over dit privacybeleid of over de verwerking van uw persoonsgegevens kunt u
      contact opnemen via:
    </p>
    <p className="pl-4 border-l-2 border-pt-gold/40">
      <strong>Ariens Design</strong> (Pecktree)
      <br />
      Zaltbommel, Nederland &middot; KvK 30274157
      <br />
      E-mail:{' '}
      <a href="mailto:privacy@pecktree.nl" className="text-pt-gold-text hover:underline">
        privacy@pecktree.nl
      </a>
    </p>
    <p>
      Indien u van mening bent dat uw persoonsgegevens onrechtmatig worden verwerkt, heeft u het
      recht een klacht in te dienen bij de <strong>Autoriteit Persoonsgegevens</strong>:
    </p>
    <p className="pl-4 border-l-2 border-pt-gold/40">
      Autoriteit Persoonsgegevens
      <br />
      Postbus 93374, 2509 AJ Den Haag
      <br />
      Telefoon: 088 - 1805 250
      <br />
      Website:{' '}
      <a
        href="https://autoriteitpersoonsgegevens.nl"
        target="_blank"
        rel="noopener noreferrer"
        className="text-pt-gold-text hover:underline"
      >
        autoriteitpersoonsgegevens.nl
      </a>
    </p>
  </article>
);

/* -------------------------------------------------------------------------- */
/*  Terms of Service (Gebruiksvoorwaarden)                                    */
/* -------------------------------------------------------------------------- */

const TermsOfService: React.FC = () => (
  <article className="prose-pt">
    <h1>Gebruiksvoorwaarden</h1>
    <p className="lead">
      Deze gebruiksvoorwaarden zijn van toepassing op het gebruik van het Axon Heritage-platform.
      Door een account aan te maken of het platform te gebruiken, gaat u akkoord met deze
      voorwaarden. Laatst bijgewerkt: oktober 2026.
    </p>

    {/* 1 */}
    <h2>1. Definities</h2>
    <ul>
      <li>
        <strong>&ldquo;Platform&rdquo;</strong> — de Pecktree-webapplicatie en bijbehorende
        diensten.
      </li>
      <li>
        <strong>&ldquo;Gebruiker&rdquo;</strong> — iedere natuurlijke of rechtspersoon die een
        account aanmaakt op het Platform.
      </li>
      <li>
        <strong>&ldquo;Genealogische Data&rdquo;</strong> — alle persoonsgegevens, relaties,
        bronvermeldingen, media en overige gegevens die de Gebruiker invoert in het Platform.
      </li>
      <li>
        <strong>&ldquo;Beheerder&rdquo;</strong> — de persoon of organisatie die de
        Pecktree-instantie beheert.
      </li>
      <li>
        <strong>&ldquo;Abonnement&rdquo;</strong> — het gekozen serviceniveau (Standard, Pro of
        Business).
      </li>
    </ul>

    {/* 2 */}
    <h2>2. Accountregistratie</h2>
    <p>
      Om gebruik te maken van het Platform dient u een account aan te maken met een geldig
      e-mailadres en een wachtwoord. U bent zelf verantwoordelijk voor:
    </p>
    <ul>
      <li>Het geheimhouden van uw inloggegevens.</li>
      <li>Alle activiteiten die onder uw account plaatsvinden.</li>
      <li>Het onmiddellijk melden van ongeautoriseerd gebruik van uw account.</li>
    </ul>
    <p>
      Pecktree behoudt zich het recht voor om accounts op te schorten of te verwijderen bij
      vermoeden van misbruik of schending van deze voorwaarden.
    </p>

    {/* 3 */}
    <h2>3. Intellectueel eigendom</h2>
    <p>
      <strong>Uw gegevens blijven van u.</strong> Alle Genealogische Data die u invoert in het
      Platform blijft uw volledige eigendom. Pecktree claimt geen eigendomsrechten op uw
      genealogische onderzoek, bronvermeldingen, media of enige andere door u ingevoerde inhoud.
    </p>
    <p>
      Het Platform zelf — inclusief de broncode, het ontwerp, logo&apos;s en handelsmerken — is
      eigendom van Pecktree en wordt beschermd door auteursrecht en andere intellectuele
      eigendomsrechten.
    </p>

    {/* 4 */}
    <h2>4. Abonnementen en tarieven</h2>
    <p>Pecktree biedt drie serviceniveaus:</p>
    <div className="overflow-x-auto my-6">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b-2 border-pt-forest/20">
            <th className="text-left py-3 pr-4 font-bold text-pt-forest">Plan</th>
            <th className="text-left py-3 pr-4 font-bold text-pt-forest">Prijs</th>
            <th className="text-left py-3 font-bold text-pt-forest">Kenmerken</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-pt-forest/10">
            <td className="py-3 pr-4 font-semibold">Standard</td>
            <td className="py-3 pr-4">Gratis</td>
            <td className="py-3">1 project, basis functionaliteit, max 500 personen</td>
          </tr>
          <tr className="border-b border-pt-forest/10">
            <td className="py-3 pr-4 font-semibold">Pro</td>
            <td className="py-3 pr-4">&euro;9,95 / maand</td>
            <td className="py-3">Onbeperkte projecten, samenwerking, prioriteitsondersteuning</td>
          </tr>
          <tr className="border-b border-pt-forest/10">
            <td className="py-3 pr-4 font-semibold">Business</td>
            <td className="py-3 pr-4">Op aanvraag</td>
            <td className="py-3">
              Dedicated instantie, SSO-integratie, SLA, onbeperkte opslag, maatwerk
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>
      Tarieven kunnen worden gewijzigd. Bij wijzigingen wordt u minimaal 30 dagen van tevoren per
      e-mail ge&iuml;nformeerd. Het Pro-abonnement wordt maandelijks gefactureerd en kan op elk
      moment worden opgezegd.
    </p>

    {/* 5 */}
    <h2>5. Herroepingsrecht</h2>
    <p>
      Bent u consument, dan heeft u het recht de overeenkomst binnen <strong>veertien dagen</strong>{' '}
      zonder opgave van redenen te ontbinden. Die termijn begint op de dag waarop u het abonnement
      afsluit. Een gratis proefperiode doet daar niets aan af: de bedenktijd loopt gewoon.
    </p>
    <p>
      Wij vragen u <strong>niet</strong> om afstand te doen van dit recht. U krijgt dus de volle
      veertien dagen, ook als u het platform in die tijd al gebruikt.
    </p>
    <p>
      Herroepen kan op twee manieren. Onder <em>Account &rsaquo; Abonnement</em> vindt u een knop om
      te herroepen of op te zeggen. U kunt ook een ondubbelzinnige verklaring sturen naar{' '}
      <a href="mailto:contact@pecktree.nl" className="text-pt-gold-text hover:underline">
        contact@pecktree.nl
      </a>
      , bijvoorbeeld met het modelformulier hieronder. Wij bevestigen de ontvangst en betalen het
      reeds betaalde bedrag binnen veertien dagen terug, met hetzelfde betaalmiddel als waarmee u
      betaalde.
    </p>
    <p>
      Heeft u ons uitdrukkelijk verzocht om tijdens de bedenktijd al te beginnen, dan bent u bij
      herroeping een bedrag verschuldigd dat evenredig is aan wat u tot dat moment heeft gebruikt.
    </p>
    <h3>Modelformulier voor herroeping</h3>
    <p>Alleen invullen en terugsturen als u de overeenkomst wilt herroepen.</p>
    <blockquote style={{ whiteSpace: 'pre-line' }}>
      {`Aan Ariens Design, Zaltbommel, contact@pecktree.nl:

Ik/Wij (*) deel/delen (*) u hierbij mede dat ik/wij (*) onze overeenkomst betreffende
de levering van de dienst Pecktree herroep/herroepen (*).

Besteld op (*)/Ontvangen op (*): ______________________
Naam consument(en): ______________________
Adres consument(en): ______________________
Handtekening consument(en) (alleen op papier): ______________________
Datum: ______________________

(*) Doorhalen wat niet van toepassing is.`}
    </blockquote>

    {/* 6 */}
    <h2>6. Aanvaardbaar gebruik</h2>
    <p>Bij het gebruik van het Platform is het niet toegestaan om:</p>
    <ul>
      <li>Het Platform te gebruiken voor illegale doeleinden of activiteiten.</li>
      <li>
        Geautomatiseerde systemen (bots, scrapers) in te zetten om gegevens van het Platform te
        verzamelen.
      </li>
      <li>De infrastructuur van het Platform opzettelijk te overbelasten of te verstoren.</li>
      <li>
        Inhoud te plaatsen die inbreuk maakt op de rechten van derden, waaronder auteursrecht en
        privacy.
      </li>
      <li>
        Beveiligingsmaatregelen te omzeilen of te proberen toegang te krijgen tot accounts of
        gegevens van andere gebruikers.
      </li>
      <li>
        Het Platform te gebruiken voor het verspreiden van malware, spam of andere schadelijke
        inhoud.
      </li>
    </ul>
    <p>
      Schending van deze regels kan leiden tot onmiddellijke opschorting of verwijdering van uw
      account, zonder restitutie van abonnementskosten.
    </p>

    {/* 7 */}
    <h2>7. Aansprakelijkheidsbeperking</h2>
    <p>
      Het Platform wordt aangeboden <strong>&ldquo;as is&rdquo;</strong> en{' '}
      <strong>&ldquo;as available&rdquo;</strong>. Pecktree geeft geen garanties met betrekking tot:
    </p>
    <ul>
      <li>De ononderbroken beschikbaarheid van het Platform.</li>
      <li>De afwezigheid van fouten of bugs in de software.</li>
      <li>
        De beschikbaarheid of nauwkeurigheid van diensten van derden (Google Maps, Open Archieven,
        Nominatim).
      </li>
    </ul>
    <p>
      Pecktree is niet aansprakelijk voor indirecte, incidentele of gevolgschade, waaronder verlies
      van gegevens, gederfde winst of schade voortvloeiend uit het gebruik of de onmogelijkheid tot
      gebruik van het Platform. De totale aansprakelijkheid van Pecktree is beperkt tot het bedrag
      dat u in de voorafgaande 12 maanden aan abonnementskosten heeft betaald.
    </p>

    {/* 8 */}
    <h2>8. Data-export en verwijdering</h2>
    <p>
      U heeft te allen tijde het recht om uw Genealogische Data te exporteren in het standaard{' '}
      <strong>GEDCOM 5.5.1</strong>-formaat. Deze functie is beschikbaar via het menu
      &ldquo;Rapporten &amp; Export&rdquo; in het Platform.
    </p>
    <p>
      U kunt uw volledige account en alle bijbehorende gegevens verwijderen via uw{' '}
      <strong>accountpagina</strong>. Na verwijdering worden al uw gegevens binnen 30 dagen
      permanent gewist uit onze systemen, in overeenstemming met uw rechten onder de AVG (zie ons{' '}
      <a href="/privacy" className="text-pt-gold-text hover:underline font-semibold">
        Privacybeleid
      </a>
      ).
    </p>

    {/* 9 */}
    <h2>9. Wijzigingen van deze voorwaarden</h2>
    <p>
      Pecktree behoudt zich het recht voor om deze gebruiksvoorwaarden te wijzigen. Bij
      substantie&euml;le wijzigingen wordt u minimaal <strong>30 dagen</strong> van tevoren per
      e-mail op de hoogte gesteld. Voortgezet gebruik van het Platform na de ingangsdatum van de
      wijzigingen houdt aanvaarding in van de gewijzigde voorwaarden.
    </p>
    <p>
      Indien u niet akkoord gaat met de gewijzigde voorwaarden, kunt u uw account opzeggen en uw
      gegevens exporteren v&oacute;&oacute;r de ingangsdatum.
    </p>

    {/* 10 */}
    <h2>10. Toepasselijk recht en geschillen</h2>
    <p>
      Op deze gebruiksvoorwaarden is <strong>Nederlands recht</strong> van toepassing. Geschillen
      die voortvloeien uit of verband houden met deze voorwaarden of het gebruik van het Platform
      worden voorgelegd aan de bevoegde rechter in het arrondissement van de statutaire
      vestigingsplaats van de Beheerder.
    </p>

    {/* 11 */}
    <h2>11. Contact</h2>
    <p>Voor vragen over deze gebruiksvoorwaarden kunt u contact opnemen via:</p>
    <p className="pl-4 border-l-2 border-pt-gold/40">
      <strong>Pecktree</strong>
      <br />
      E-mail:{' '}
      <a href="mailto:info@pecktree.nl" className="text-pt-gold-text hover:underline">
        info@pecktree.nl
      </a>
    </p>
  </article>
);

export default LegalPages;
